import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Pressable, View } from 'react-native';

import { Button, HStack, Text, VStack, theme } from '@squire/ui';

import { CheckIcon } from '@/components/_icons';
import {
  BottomSheet,
  BottomSheetInput,
  type BottomSheetRef,
} from '@/components/_shared/BottomSheet';
import { useToast } from '@/components/_shared/Toaster';
import { useSubmitNPSSurveyMutation } from '@/hooks/mutations/surveys/useSubmitNPSSurveyMutation';
import { useOnMount } from '@/hooks/shared/useOnMount';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { pressedState } from '@/utils/a11y';
import { getApiPlatform } from '@/utils/getPlatform';
import { Tracker } from '@/utils/tracking';

import { styles } from './styles';
import type { NPSSurveyModalProps, NPSSurveyStep } from './types';

const SCORE_ROW_1 = [0, 1, 2, 3, 4, 5];
const SCORE_ROW_2 = [6, 7, 8, 9, 10];

export const NPSSurveyModal = forwardRef<BottomSheetRef, NPSSurveyModalProps>(
  ({ campaign, userId, onDismiss }, ref) => {
    const { t } = useScreenTranslations('NPSSurveyModal');
    const { toast } = useToast();
    const internalRef = useRef<BottomSheetRef>(null);

    const [step, setStep] = useState<NPSSurveyStep>('score-selection');
    const [selectedScore, setSelectedScore] = useState<number | null>(null);
    const [feedback, setFeedback] = useState('');

    useImperativeHandle(ref, () => ({
      open: (animated) => internalRef.current?.open(animated),
      close: (animated) => internalRef.current?.close(animated),
      snapTo: (index, animated) => internalRef.current?.snapTo(index, animated),
      snapToIndex: (index, animated) =>
        internalRef.current?.snapToIndex(index, animated),
    }));

    useOnMount(() => {
      internalRef.current?.open();
      Tracker.track('nps_survey_shown', { campaignId: campaign.id });
    });

    const { mutate, isPending } = useSubmitNPSSurveyMutation({
      onError: () => toast({ variant: 'error', message: t('submitError') }),
    });

    const scaleQuestion = campaign.survey.questions.find(
      (q) => q.type === 'scale_11',
    );
    const textQuestion = campaign.survey.questions.find(
      (q) => q.type === 'text',
    );

    const handleScoreSelect = (score: number) => {
      setSelectedScore(score);
      setFeedback('');
      Tracker.track('nps_score_selected', {
        campaignId: campaign.id,
        score: score.toString(),
      });
      setStep('feedback');
    };

    // Fires after BottomSheet close animation completes (swipe or programmatic).
    const handleSheetDismissed = () => {
      Tracker.track('nps_survey_dismissed', { campaignId: campaign.id });
      onDismiss();
    };

    const handleMaybeLaterPress = () => {
      internalRef.current?.close();
    };

    const handleDone = () => {
      if (selectedScore === null || !scaleQuestion) return;

      const answers = [
        { id: scaleQuestion.id, answer: selectedScore.toString() },
        ...(textQuestion && feedback.trim()
          ? [{ id: textQuestion.id, answer: feedback }]
          : []),
      ];

      const platform = {
        iOS: 'iPhone',
        Android: 'Android',
        Web: 'Web',
      }[getApiPlatform()];

      mutate(
        {
          userCampaignId: campaign.userCampaignId,
          userId,
          platform,
          answers,
        },
        {
          onSuccess: () => {
            toast({ variant: 'success', message: t('thankYouToast') });
            onDismiss();
          },
        },
      );
    };

    const renderScoreButton = (score: number) => {
      const isSelected = selectedScore === score;
      return (
        <Pressable
          key={score}
          testID={`score-button-${score}`}
          onPress={() => handleScoreSelect(score)}
          accessibilityRole="button"
          {...pressedState(isSelected)}
          style={[styles.scoreButton, isSelected && styles.scoreButtonSelected]}
        >
          <Text
            variant="mStrong"
            color={isSelected ? 'textPrimaryInverse' : 'textPrimary'}
          >
            {score.toString()}
          </Text>
        </Pressable>
      );
    };

    const renderScoreSection = () => (
      <>
        <VStack style={styles.header}>
          <Text variant="3xlStrong" color="textPrimary" style={styles.title}>
            {t('title')}
          </Text>
          <Text variant="lStrong" color="textPrimary" style={styles.question}>
            {scaleQuestion?.question ?? t('question')}
          </Text>
        </VStack>

        <VStack style={styles.scoresContainer}>
          <HStack style={styles.scaleLegend}>
            <Text variant="s" color="textSecondary">
              {t('fromNotLikely')}
            </Text>
            <View style={styles.scaleBadge}>
              <Text variant="s" color="textPrimary">
                0
              </Text>
            </View>
            <Text variant="s" color="textSecondary">
              {t('toMostLikely')}
            </Text>
            <View style={styles.scaleBadge}>
              <Text variant="s" color="textPrimary">
                10
              </Text>
            </View>
          </HStack>
          <HStack style={styles.scoresRow}>
            {SCORE_ROW_1.map(renderScoreButton)}
          </HStack>
          <HStack style={styles.scoresRow}>
            {SCORE_ROW_2.map(renderScoreButton)}
          </HStack>
        </VStack>
      </>
    );

    return (
      <BottomSheet
        ref={internalRef}
        onDismiss={handleSheetDismissed}
        dismissible
      >
        <VStack style={styles.content}>
          {step === 'score-selection' && (
            <>
              {renderScoreSection()}
              <Pressable
                testID="maybe-later-button"
                onPress={handleMaybeLaterPress}
                style={styles.maybeLater}
                accessibilityRole="button"
              >
                <Text variant="m" color="textLink">
                  {t('maybeLater')}
                </Text>
              </Pressable>
            </>
          )}

          {step === 'feedback' && (
            <VStack testID="feedback-view" style={styles.feedbackContainer}>
              <Text variant="3xl" color="textPrimary">
                {t('thankYouTitle')}
              </Text>
              <View style={styles.checkIconWrapper}>
                <View style={styles.checkIcon}>
                  <CheckIcon color="textPrimaryInverse" size={20} />
                </View>
              </View>
              {textQuestion && (
                <>
                  <Text
                    variant="lStrong"
                    color="textPrimary"
                    style={styles.anythingElse}
                  >
                    {textQuestion.question}
                  </Text>

                  <VStack space={theme.spacing[3]}>
                    <Text variant="sStrong" color="textSecondary">
                      {t('reasonLabel')}
                    </Text>
                    <BottomSheetInput
                      testID="feedback-input"
                      value={feedback}
                      onChangeText={setFeedback}
                      placeholder={t('reasonPlaceholder')}
                      multiline
                      containerStyle={styles.feedbackInput}
                    />
                  </VStack>
                </>
              )}

              <Button
                testID="done-button"
                variant="primary"
                onPress={handleDone}
                disabled={isPending}
              >
                {t('done')}
              </Button>
            </VStack>
          )}
        </VStack>
      </BottomSheet>
    );
  },
);

NPSSurveyModal.displayName = 'NPSSurveyModal';
